import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { listEmbeddingModels, listRerankModels } from '../api/settings'

type Props = {
  apiBaseUrl: string
  apiKey?: string
  authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  value: string
  onChange: (model: string) => void
  inputClassName: string
  disabled?: boolean
  id?: string
  /** What kind of model this picker loads — drives copy and toast text. Defaults to 'embedding'. */
  kind?: 'embedding' | 'llm' | 'rerank'
  /** Defaults to `listEmbeddingModels`; pass `listLlmModels` for chat/completion models. */
  loadModels?: (data: {
    apiBaseUrl: string
    apiKey?: string | null
    authHeader?: 'authorization_bearer' | 'api_key' | 'none'
  }) => Promise<string[]>
  /** Extra options injected at the top of the suggestions (e.g. a local ONNX sentinel). */
  extraOptions?: Array<{ value: string; label: string }>
  required?: boolean
  placeholder?: string
}

const MAX_VISIBLE = 50

export default function ModelPicker({
  apiBaseUrl,
  apiKey,
  authHeader,
  value,
  onChange,
  inputClassName,
  disabled = false,
  id = 'model',
  kind = 'embedding',
  loadModels,
  extraOptions = [],
  required = true,
  placeholder = 'model-id — type to filter, or enter manually',
}: Props) {
  const [loaded, setLoaded] = useState<{ url: string; models: string[] } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const resolvedLoadModels =
    loadModels ?? (kind === 'rerank' ? listRerankModels : listEmbeddingModels)
  const kindLabel = kind === 'llm' ? 'LLM' : kind === 'rerank' ? 'reranking' : 'embedding'
  const fieldLabel =
    kind === 'llm' ? 'LLM Model' : kind === 'rerank' ? 'Reranker model' : 'Embedding Model'

  const trimmedUrl = apiBaseUrl.trim()
  const loadedModels = loaded?.url === trimmedUrl ? loaded.models : null
  const canLoad = Boolean(trimmedUrl) && !disabled

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  // Type-to-filter over extra options + loaded models (case-insensitive
  // substring match). Free-text entry is always allowed — the dropdown is
  // suggestions only.
  const filtered = useMemo((): Array<{ value: string; label?: string }> => {
    const query = value.trim().toLowerCase()
    const extras: Array<{ value: string; label?: string }> = extraOptions.map((o) => ({
      value: o.value,
      label: o.label,
    }))
    const fromLoaded: Array<{ value: string; label?: string }> = (loadedModels ?? []).map((m) => ({
      value: m,
    }))
    const all = [...extras, ...fromLoaded]
    const seen = new Set<string>()
    const deduped = all.filter((o) => {
      if (seen.has(o.value)) return false
      seen.add(o.value)
      return true
    })
    if (!query) return deduped.slice(0, MAX_VISIBLE)
    return deduped.filter((o) => o.value.toLowerCase().includes(query)).slice(0, MAX_VISIBLE)
  }, [value, loadedModels, extraOptions])

  // Reset keyboard highlight whenever the result set changes (render-time
  // adjustment — no effect needed).
  const prevFilteredLength = useRef(filtered.length)
  if (prevFilteredLength.current !== filtered.length) {
    prevFilteredLength.current = filtered.length
    if (highlight !== 0) setHighlight(0)
  }

  const handleLoad = async () => {
    if (!canLoad) return
    setIsLoading(true)
    try {
      const models = await resolvedLoadModels({
        apiBaseUrl: trimmedUrl,
        apiKey: apiKey?.trim() || undefined,
        ...(authHeader ? { authHeader } : {}),
      })
      if (models.length === 0) {
        setLoaded(null)
        toast.error(`No ${kindLabel} models found — enter a model name manually`)
        return
      }
      setLoaded({ url: trimmedUrl, models })
      // Keep a manually typed id intact — only default to the first loaded
      // model when the field is still empty.
      if (!value && !extraOptions.some((o) => o.value === value)) {
        onChange(models[0] ?? value)
      }
      setIsOpen(true)
      toast.success(`Loaded ${models.length} ${kindLabel} model${models.length === 1 ? '' : 's'}`)
    } catch (error) {
      setLoaded(null)
      toast.error(error instanceof Error ? error.message : 'Failed to load models')
    } finally {
      setIsLoading(false)
    }
  }

  const commitSelection = (model: string) => {
    onChange(model)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && filtered.length > 0) {
      e.preventDefault()
      setIsOpen(true)
      setHighlight((h) => (h + 1) % filtered.length)
    } else if (e.key === 'ArrowUp' && filtered.length > 0) {
      e.preventDefault()
      setIsOpen(true)
      setHighlight((h) => (h - 1 + filtered.length) % filtered.length)
    } else if (e.key === 'Enter' && isOpen && filtered.length > 0) {
      const selected = filtered[highlight]
      if (selected && selected.value.toLowerCase() !== value.trim().toLowerCase()) {
        e.preventDefault()
        commitSelection(selected.value)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1 min-w-0">
          <label htmlFor={id} className="block text-sm font-medium text-harbor-text-secondary mb-1">
            {fieldLabel}
          </label>
          <div ref={containerRef} className="relative">
            <input
              id={id}
              type="text"
              value={value}
              onChange={(e) => {
                onChange(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => {
                if (filtered.length > 0) setIsOpen(true)
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className={inputClassName}
              placeholder={placeholder}
              required={required}
              autoComplete="off"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={`${id}-listbox`}
              aria-autocomplete="list"
            />
            {isOpen && filtered.length > 0 && (
              <ul
                id={`${id}-listbox`}
                role="listbox"
                className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-harbor-border bg-harbor-surface shadow-xl py-1"
              >
                {filtered.map((o, i) => (
                  <li key={o.value} role="option" aria-selected={i === highlight}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        // Commit before the input blurs.
                        e.preventDefault()
                        commitSelection(o.value)
                      }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`w-full text-left px-3 py-1.5 text-sm truncate ${
                        i === highlight
                          ? 'bg-emerald-600/20 text-harbor-text'
                          : 'text-harbor-text-secondary'
                      }`}
                      title={o.value}
                    >
                      <span className="font-mono text-xs">{o.value}</span>
                      {typeof o.label === 'string' && o.label !== o.value && (
                        <span className="block text-[11px] text-harbor-text-muted truncate">
                          {o.label}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void handleLoad()}
          disabled={!canLoad || isLoading}
          className="shrink-0 px-3 py-2.5 text-sm font-medium rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Loading…' : loadedModels ? 'Reload' : 'Load models'}
        </button>
      </div>
      <p className="text-xs text-harbor-text-muted">
        {loadedModels
          ? `${loadedModels.length} ${kindLabel} models loaded — type to filter, or enter any id manually.`
          : `Loads ${kindLabel} models from `}{' '}
        {!loadedModels && (
          <>
            <span className="font-mono">GET /models</span> on the API base URL — then type to
            filter.
          </>
        )}
      </p>
    </div>
  )
}
